import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Trash2, Edit, Eye, Calendar, Clock, MapPin, 
  Users, DollarSign, Star, MoreVertical, Search, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const EventManagement = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading, refreshUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    venue_id: '',
    venue_name: '',
    date: '',
    time: '',
    price: '',
    gold_price: '',
    gold_exclusive: false,
    category: 'trending',
    capacity: '100'
  });

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      // Allow both partners and admins to access
      if (!isPartner && user?.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      
      fetchData();
    };
    
    init();
  }, [isAuthenticated, isPartner, authLoading, user]);

  const fetchData = async () => {
    try {
      const [dashboardRes, venuesRes] = await Promise.all([
        api.get('/partner/dashboard'),
        api.get('/venues')
      ]);
      setEvents(dashboardRes.data.events);
      setVenues(venuesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const selectedVenue = venues.find(v => v.id === formData.venue_id);
      const payload = {
        ...formData,
        venue_name: selectedVenue?.name || formData.venue_name,
        price: parseFloat(formData.price),
        gold_price: formData.gold_price ? parseFloat(formData.gold_price) : null,
        capacity: parseInt(formData.capacity),
        images: []
      };
      
      await api.post('/events', payload);
      toast.success('Event created successfully!');
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      venue_id: '',
      venue_name: '',
      date: '',
      time: '',
      price: '',
      gold_price: '',
      gold_exclusive: false,
      category: 'trending',
      capacity: '100'
    });
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(search.toLowerCase()) ||
    event.venue_name.toLowerCase().includes(search.toLowerCase())
  );

  if (!isAuthenticated || (!isPartner && user?.role !== 'admin')) return null;

  return (
    <PartnerLayout 
      title="Events"
      subtitle="Create and manage your events"
      actions={
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Jazz Night at The Loft"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venue">Venue *</Label>
                  <Select 
                    value={formData.venue_id} 
                    onValueChange={(v) => {
                      const venue = venues.find(ven => ven.id === v);
                      setFormData({ 
                        ...formData, 
                        venue_id: v,
                        venue_name: venue?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select venue" />
                    </SelectTrigger>
                    <SelectContent>
                      {venues.map((venue) => (
                        <SelectItem key={venue.id} value={venue.id}>
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trending">Trending</SelectItem>
                      <SelectItem value="date_night">Date Night</SelectItem>
                      <SelectItem value="craft_beer">Craft Beer</SelectItem>
                      <SelectItem value="live_music">Live Music</SelectItem>
                      <SelectItem value="art">Art & Culture</SelectItem>
                      <SelectItem value="food">Food & Dining</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="35.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gold_price">Gold Price ($)</Label>
                  <Input
                    id="gold_price"
                    type="number"
                    step="0.01"
                    value={formData.gold_price}
                    onChange={(e) => setFormData({ ...formData, gold_price: e.target.value })}
                    placeholder="25.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capacity">Capacity *</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your event..."
                  required
                />
              </div>

              <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <input
                  type="checkbox"
                  id="gold_exclusive"
                  checked={formData.gold_exclusive}
                  onChange={(e) => setFormData({ ...formData, gold_exclusive: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="gold_exclusive" className="cursor-pointer flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  Gold Members Only Event
                </Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Event'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Events Grid */}
      {filteredEvents.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-bold text-foreground mb-2">No events yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first event to start selling tickets
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <Card 
              key={event.id} 
              className="overflow-hidden hover:shadow-lg transition-shadow group cursor-pointer"
              onClick={() => navigate(`/partner/events/${event.id}`)}
            >
              <div className="aspect-video bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-950 dark:to-purple-950 relative">
                {event.images?.[0] ? (
                  <img src={event.images[0]} alt={event.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Calendar className="w-12 h-12 text-indigo-400" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/partner/events/${event.id}`); }}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Manage Event
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/partner/events/${event.id}/builder`); }}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Page
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.id}`); }}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Public Page
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {event.gold_exclusive && (
                  <Badge className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 border-0">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    GOLD
                  </Badge>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-foreground mb-2 line-clamp-1">{event.title}</h3>
                <div className="space-y-1 text-sm text-muted-foreground mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {event.date}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {event.time}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {event.venue_name}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">
                      <span className="font-bold text-foreground">{event.tickets_sold}</span>
                      <span className="text-muted-foreground">/{event.capacity}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-bold text-green-600">
                      {(event.tickets_sold * event.price).toFixed(0)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PartnerLayout>
  );
};

export default EventManagement;
